import { useCallback, useRef, useState } from 'react';

import { useSessionResult } from '../SessionSummary/useSessionResult';
import type { ProgramApiFetch } from '../ProgramEditor/types';

/** The imperative handle the host's post-session summary card exposes for
 *  the post-save-row share affordance. RW-18 + the 2026-08-18 share-
 *  card web lift: `ShareCardRenderer` + the capture/share state machine
 *  (`useShareCardHandle`) are both shared (`packages/ui/src/Logger/`) —
 *  mobile and web-rnw each wire this handle to a real `share()`, only the
 *  post-capture step differs (native share sheet vs. Web Share API/
 *  download, see apps/web-rnw/src/logger/shareCardCaptureWeb.ts). */
export type PostSessionSummaryHandle = {
  share: () => Promise<void>;
  /** Whether a capture is currently in flight (disables the host's own
   *  in-card Share button while true). Required so every host's ref
   *  shape is structurally identical — not read by usePostSaveSummary
   *  itself, only by whatever the host renders inside the card. */
  isSharing: () => boolean;
};

/** What the Logger shows after a 'sent' save. `maxProposed`: the exercises
 *  the save proposed a new MAX for (#182), named in one line. */
export type PostSaveSummaryState = {
  sessionId: string;
  onDone: () => void;
  maxProposed?: readonly string[];
  /** #511: the texts of notes that did not save, shown whole and selectable
   *  so the athlete can copy them. */
  failedNotes?: readonly string[];
};

// Post-save summary + share state, extracted from useLoggerScreen (500-line
// cap). One concept: what the Logger shows AFTER a save instead of closing,
// and the imperative handle the post-save Share button reaches the card
// through. #318: only there. A past session's share is on its summary, before
// the editor opens.
//
// `setPostSaveSummary` goes back out because useSaveWorkout writes it on a
// 'sent' save — the summary is raised BY the save, not by this hook.
export function usePostSaveSummary(apiFetch: ProgramApiFetch) {
  // Post-save summary state. After a 'sent' save we don't close the
  // modal immediately — we switch the body into "summary mode" so the
  // user sees the PostSessionSummaryCard with Share + Done CTAs.
  // 'queued' saves skip the summary (no server-computed SessionResult
  // available yet) and follow the old close-immediately path.
  const [postSaveSummary, setPostSaveSummary] =
    useState<PostSaveSummaryState | null>(null);
  const summarySessionId = postSaveSummary?.sessionId ?? null;
  const sessionResult = useSessionResult({ apiFetch, sessionId: summarySessionId });
  // Imperative ref into the host's post-session summary card for the
  // post-save Share button. Card is only mounted when sessionResult is
  // present, so callers must null-check before invoking.
  const summaryCardRef = useRef<PostSessionSummaryHandle>(null);
  const [postSaveSharing, setPostSaveSharing] = useState(false);
  const sharePostSave = useCallback(async () => {
    if (!summaryCardRef.current) return;
    setPostSaveSharing(true);
    try {
      await summaryCardRef.current.share();
    } finally {
      setPostSaveSharing(false);
    }
  }, []);

  return {
    postSaveSummary,
    setPostSaveSummary,
    sessionResult,
    summaryCardRef,
    postSaveSharing,
    sharePostSave,
  };
}
